import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Link } from 'react-router-dom';
import { api, type Andamento } from '../api';
import { Caricamento, Errore, Importo, Scheda, useCarico } from '../componenti/base';
import { euroCompatto, formattaEuro, formattaData } from '../formato';
import { useSessione } from '../main';

/**
 * L'andamento economico dell'esercizio.
 *
 * Il previsionale dice **quando** i soldi si muovono; qui si dice **se il
 * lavoro sta pagando**. Sono due domande diverse e possono divergere per mesi
 * senza che nessuno se ne accorga: è esattamente il caso di un'azienda che
 * fattura bene e incassa male.
 */
export function AndamentoEconomico() {
  const { organizzazione } = useSessione();
  const { dati, caricamento, errore, ricarica } = useCarico(() => api.get<Andamento>('/api/andamento'), []);

  if (caricamento) return <Caricamento />;
  if (errore || !dati) return <Errore testo={errore ?? 'Andamento non disponibile'} riprova={ricarica} />;

  const { competenza, cassa, scartoCent, tesoreria, pareggio, affidabilita, mesi } = dati;
  const quotaGenerica =
    affidabilita.costiTotaliCent > 0 ? affidabilita.costiSuVocePredefinitaCent / affidabilita.costiTotaliCent : 0;
  // Sopra la metà dei costi sulla voce predefinita, «fisso» e «variabile»
  // descrivono il secchio e non l'azienda: la cifra si mostra lo stesso — è
  // quella che i dati danno — ma detta com'è.
  const pareggioAttendibile = quotaGenerica < 0.5;

  const serie = mesi.map((m) => ({
    nome: m.etichetta,
    ricavi: m.ricaviCent / 100,
    margine: m.margineEbitda === null ? null : Math.round(m.margineEbitda * 100),
  }));

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Cifra
          titolo="EBITDA di competenza"
          cent={competenza.ebitdaCent}
          nota={competenza.margineEbitda === null ? 'dalle fatture' : `${(competenza.margineEbitda * 100).toFixed(1)} % dei ricavi`}
        />
        <Cifra titolo="EBITDA di cassa" cent={cassa.ebitdaCent} nota="da ciò che è passato in banca" />
        <Cifra
          titolo="Scarto"
          cent={scartoCent}
          tono="neutro"
          nota={scartoCent > 0 ? 'lavoro fatto e non ancora incassato' : 'incassato più di quanto fatturato'}
        />
        <Cifra titolo="Ricavi dell’esercizio" cent={competenza.ricaviCent} tono="incassi" nota={`dal ${formattaData(dati.dal, organizzazione.formatoData)}`} />
      </div>

      {/* La diagnosi, quando i due numeri raccontano storie diverse. È il motivo
          per cui si calcolano tutti e due: un EBITDA solo non distingue
          «non guadagno» da «non incasso», e sono due malattie con due cure. */}
      {competenza.ebitdaCent > 0 && cassa.ebitdaCent < 0 && (
        <div className="rounded-md border border-attenzione/30 bg-attenzione-chiaro px-4 py-3 text-sm text-attenzione">
          <strong>Il lavoro paga, l’incasso no.</strong> Per competenza l’esercizio è in utile di{' '}
          <span className="num">{formattaEuro(competenza.ebitdaCent)}</span>, ma in banca è passato{' '}
          <span className="num">{formattaEuro(cassa.ebitdaCent)}</span>. La differenza è lavoro già fatto e fatturato
          che non è ancora diventato denaro: non è un problema di redditività, è un problema di incasso.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Scheda titolo="Tesoreria e autonomia">
          <p className="text-sm text-attenuato">Per quanto la cassa di oggi copre le uscite al ritmo attuale.</p>
          <p className="mt-3">
            <span className="text-3xl font-semibold">
              {tesoreria.mesiDiAutonomia === null ? '—' : tesoreria.mesiDiAutonomia.toFixed(1)}
            </span>{' '}
            <span className="text-inchiostro-600">mesi di uscite coperte</span>
          </p>
          <p className="mt-1 text-sm text-inchiostro-600">
            <span className="num">{formattaEuro(tesoreria.liquiditaCent)}</span> in cassa, contro{' '}
            <span className="num">{formattaEuro(tesoreria.usciteMensiliCent)}</span> di uscite al mese.
          </p>
          <Barra
            valore={tesoreria.mesiDiAutonomia}
            soglie={[3, 6]}
            massimo={12}
            etichette={['critico', 'attenzione', 'solido']}
          />
        </Scheda>

        <Scheda titolo="Punto di pareggio">
          <p className="text-sm text-attenuato">Quanto serve fatturare perché i ricavi coprano tutti i costi.</p>
          <p className="mt-3">
            <span className="num text-3xl font-semibold">
              {pareggio.puntoDiPareggioCent === null ? '—' : euroCompatto(pareggio.puntoDiPareggioCent)}
            </span>{' '}
            <span className="text-inchiostro-600">di ricavi minimi</span>
          </p>
          {pareggio.puntoDiPareggioCent !== null && (
            <p className="mt-1 text-sm text-inchiostro-600">
              Costi fissi <span className="num">{formattaEuro(pareggio.costiFissiCent)}</span>, e{' '}
              <span className="num">{((pareggio.quotaVariabile ?? 0) * 100).toFixed(0)} %</span> di ogni euro fatturato
              se ne va in costi che crescono col lavoro.
            </p>
          )}
          {!pareggioAttendibile && (
            <p className="mt-3 rounded-md bg-attenzione-chiaro px-3 py-2 text-xs text-attenzione">
              <strong>Questa cifra non è ancora affidabile.</strong> Il{' '}
              <span className="num">{(quotaGenerica * 100).toFixed(0)} %</span> dei costi finisce su una voce
              predefinita perché nessuno dei{' '}
              <span className="num">{affidabilita.fornitori - affidabilita.fornitoriConVoce}</span> fornitori ne ha una
              propria: con quasi tutti i costi nello stesso secchio, «fisso» e «variabile» descrivono il secchio, non
              l’azienda.{' '}
              <Link to="/anagrafiche" className="underline decoration-dotted underline-offset-2">
                Assegna una voce ai fornitori
              </Link>{' '}
              e il pareggio diventa un numero vero.
            </p>
          )}
        </Scheda>
      </div>

      <Scheda titolo="Ricavi mensili e margine EBITDA">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="e" tick={{ fontSize: 11 }} tickFormatter={(v: number) => euroCompatto(v * 100)} />
              <YAxis
                yAxisId="p"
                orientation="right"
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => `${v} %`}
              />
              <Tooltip
                formatter={(v, nome) => {
                  if (typeof v !== 'number') return '—';
                  return nome === 'Margine EBITDA' ? `${v} %` : formattaEuro(v * 100);
                }}
              />
              <ReferenceLine yAxisId="p" y={0} stroke="#9ca3af" />
              <Bar yAxisId="e" dataKey="ricavi" name="Ricavi" fill="var(--color-incassi)" radius={[3, 3, 0, 0]} />
              <Line
                yAxisId="p"
                type="monotone"
                dataKey="margine"
                name="Margine EBITDA"
                stroke="#111827"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Scheda>

    </div>
  );
}

function Cifra({
  titolo,
  cent,
  nota,
  tono = 'auto',
}: {
  titolo: string;
  cent: number;
  nota: string;
  tono?: 'auto' | 'neutro' | 'incassi';
}) {
  return (
    <div className="scheda border-t-inchiostro-300 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">{titolo}</p>
      <p className="mt-1 text-2xl">
        {/* Lo scarto non è né un incasso né un'uscita: è lavoro fatto che non è
            ancora denaro. Dipingerlo del verde dei soldi buoni lo farebbe
            leggere come una notizia positiva, che non è. */}
        {tono === 'neutro' ? (
          <span className="num font-semibold text-inchiostro-800">{formattaEuro(cent)}</span>
        ) : (
          <Importo cent={cent} tono={tono === 'incassi' ? 'incassi' : 'auto'} grassetto />
        )}
      </p>
      <p className="mt-0.5 text-xs text-inchiostro-500">{nota}</p>
    </div>
  );
}

/**
 * La barra a tre fasce. Il segno si posa dove sta il valore, e le soglie sono
 * scritte sotto: una barra colorata senza i numeri costringe a fidarsi del
 * colore, che è esattamente ciò che non si vuole su un dato di cassa.
 */
function Barra({
  valore,
  soglie,
  massimo,
  etichette,
}: {
  valore: number | null;
  soglie: [number, number];
  massimo: number;
  etichette: [string, string, string];
}) {
  const quota = valore === null ? 0 : Math.min(valore / massimo, 1);
  const larghezze = [soglie[0] / massimo, (soglie[1] - soglie[0]) / massimo, 1 - soglie[1] / massimo];
  const colori = ['bg-allerta/30', 'bg-attenzione/30', 'bg-incassi/30'];
  return (
    <div className="mt-4">
      <div className="relative flex h-2 gap-1">
        {larghezze.map((l, i) => (
          <div key={i} className={`rounded-full ${colori[i]}`} style={{ width: `${l * 100}%` }} />
        ))}
        {valore !== null && (
          <span
            className="absolute -top-1 h-4 w-1 rounded-sm bg-inchiostro-800"
            style={{ left: `calc(${quota * 100}% - 2px)` }}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-inchiostro-500">
        {etichette.map((e) => (
          <span key={e}>{e}</span>
        ))}
      </div>
    </div>
  );
}
